import ApiClient from 'api-client/ApiClient'
import ActionsSet from 'core/actions/ActionsSet'
import CreateAction from 'core/actions/CreateAction'
import DeleteAction from 'core/actions/DeleteAction'
import ListAction from 'core/actions/ListAction'
import DataKeys, { entityNamesByKey } from 'k8s/DataKeys'
import Bugsnag from 'utils/bugsnag'
import { pathStr } from 'utils/fp'
import { trackEvent } from 'utils/tracking'
import UpdateAction from 'core/actions/UpdateAction'

const { kubernetes } = ApiClient.getInstance()

export const kaapiClusterActions = ActionsSet.make<DataKeys.KaapiClusters>({
  uniqueIdentifier: 'metadata.uid',
  entityName: entityNamesByKey[DataKeys.KaapiClusters],
  cacheKey: DataKeys.KaapiClusters,
})

export const listKaapiClusters = kaapiClusterActions.add(
  new ListAction<DataKeys.KaapiClusters>(async ({ namespace }) => {
    Bugsnag.leaveBreadcrumb('Attempting to get Kaapi Clusters ', { namespace })
    return kubernetes.getAllCustomResources(DataKeys.KaapiClusters)
  })
    .addDependency(DataKeys.KaapiHostedControlPlanes)
    .addDependency(DataKeys.KaapiMachineDeployments)
    .addDependency(DataKeys.KaapiOpenStackMachineTemplates)
    .addDependency(DataKeys.KaapiMachines)
    .addDependency(DataKeys.KaapiBYOClusters)
    .addDependency(DataKeys.KaapiBYOMachineTemplates)
    .addDependency(DataKeys.KaapiClusterUpgradeJobs),
)

export const deleteKaapiCluster = kaapiClusterActions.add(
  new DeleteAction<DataKeys.KaapiClusters, { namespace: string; name: string }>(
    async ({ namespace, name }) => {
      Bugsnag.leaveBreadcrumb('Attempting to delete Kaapi cluster', { namespace, name })
      await kubernetes.deleteCustomResource({ type: DataKeys.KaapiClusters, name })
    },
  ),
)

export const createKaapiCluster = kaapiClusterActions.add(
  new CreateAction<DataKeys.KaapiClusters, { namespace: string; body: any; clusterName: string }>(
    async ({ namespace, body, clusterName }) => {
      try {
        Bugsnag.leaveBreadcrumb('Attempting to create Kaapi Cluster', {
          namespace,
          body,
          clusterName,
        })
        const created = await kubernetes.createCustomResource({
          type: DataKeys.KaapiClusters,
          body,
        })
        trackEvent('Create New Kaapi Cluster', {
          namespace,
          name: pathStr('metadata.name', created),
        })
        return created
      } catch (e) {
        Bugsnag.leaveBreadcrumb('Failed to create Kaapi Cluster', {
          namespace,
          body,
          clusterName,
        })
        throw new Error('Failed to create cluster')
      }
    },
  ),
)

export const updateNodePools = kaapiClusterActions.add(
  new UpdateAction<DataKeys.KaapiClusters, { namespace: string; body: any; clusterName: string }>(
    async ({ namespace, body, clusterName }) => {
      try {
        Bugsnag.leaveBreadcrumb(
          'Attempting to add Kaapi Cluster Node Group with new cluster class API',
          {
            namespace,
            body,
          },
        )
        const created = await kubernetes.patchCustomResource({
          name: clusterName,
          type: DataKeys.KaapiClusters,
          body,
        })
        trackEvent('Update New Kaapi Cluster Node Group with new cluster class API', {
          namespace,
          name: pathStr('metadata.name', created),
        })
        return created
      } catch (err) {
        Bugsnag.leaveBreadcrumb(
          'Failed to update Kaapi Cluster Node Group with new cluster class API',
          {
            namespace,
            body,
          },
        )
        throw new Error('Failed to update Node Group')
      }
    },
  ),
)
